import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../contexts/NotificationContext";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    CardFooter,
    Form,
    FormGroup,
    Input,
    Row,
    Col,
    Label,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateTenantUser } from "api/tenant/service";
import { formatPhoneNumber, validatePhoneNumber } from "../../utils/phoneUtils";
import { getActiveColorStyle } from "../../utils/colorUtils";
import Select from "components/Select";
import { useGetRoles } from "api/roles/service";

const initialForm = {
    email: "",
    alt_email: "",
    password: "",
    full_name: "",
    address: "",
    salutation: "",
    gender: "",
    suffix: "",
    title: "",
    work_phone: "",
    home_phone: "",
    cell_phone: "",
    fax: "",
};

const suffixOptions = [
    "",
    "Jr.",
    "Sr.",
    "I",
    "II",
    "III",
    "IV",
    "V",
];

const salutationOptions = [
    "",
    "Mr.",
    "Mrs.",
    "Ms.",
    "Dr.",
    "Prof.",
    "Rev.",
    "Sir",
    "Madam",
];

const genderOptions = [
    "",
    "Male",
    "Female",
    "Other",
    "Prefer not to say"
];

const TenantUserAdd = ({ activeColor = "secondary" }) => {
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

    const navigate = useNavigate();
    const { tenantId } = useParams();

    const createTenantUserMutation = useCreateTenantUser(tenantId);
    const { data: rolesData, isLoading: rolesLoading } = useGetRoles();

    const [formData, setFormData] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [selectedRole, setSelectedRole] = useState("");

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Apply phone formatting for phone fields
        let formattedValue = value;
        if (name === 'work_phone' || name === 'home_phone' || name === 'cell_phone') {
            formattedValue = formatPhoneNumber(value);
        }

        setFormData((prev) => ({ ...prev, [name]: formattedValue }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const missingFields = [];

        if (!formData.email) {
            newErrors.email = t('userAdd.errors.emailRequired') || 'Email Required';
            missingFields.push(t('userAdd.labels.email') || 'Email');
        }
        if (!formData.full_name) {
            newErrors.full_name = t('userAdd.errors.fullNameRequired') || 'Full Name Required';
            missingFields.push(t('userAdd.labels.fullName') || 'Full Name');
        }
        if (!selectedRole) {
            newErrors.user_role = t('userAdd.errors.roleRequired') || 'Role Required';
            missingFields.push(t('userAdd.labels.userRole') || 'User Role');
        }

        // Phone validation using utility
        const phoneError = validatePhoneNumber(formData.work_phone);
        if (phoneError) {
            newErrors.work_phone = phoneError;
            missingFields.push(t('userAdd.labels.workPhone') || 'Work Phone');
        }

        if (!formData.password) {
            newErrors.password = t('userAdd.errors.passwordRequired') || 'Password Required';
            missingFields.push(t('userAdd.labels.password') || 'Password');
        } else if (formData.password.length < 6) {
            newErrors.password = t('userAdd.errors.passwordMinLength') || 'Password must be at least 6 characters long';
            missingFields.push(t('userAdd.labels.password') || 'Password');
        }

        setErrors(newErrors);

        // Show notification if there are errors
        if (Object.keys(newErrors).length > 0) {
            const fieldsList = missingFields.join(', ');
            showNotification(
                'danger',
                `${t('userAdd.requiredFieldsMissing') || 'Required fields missing'}: ${fieldsList}`
            );
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const roleEntity = rolesData?.results?.find((r) => r.documentId === selectedRole || r.id === selectedRole);
        if (!roleEntity?.id && !roleEntity?.documentId) {
            setErrors((prev) => ({ ...prev, user_role: "Invalid role selection" }));
            return;
        }

        const payload = {
            tenantId: tenantId,
            email: formData.email,
            password: formData.password,
            full_name: formData.full_name,
            address: formData.address,
            salutation: formData.salutation,
            gender: formData.gender,
            suffix: formData.suffix,
            title: formData.title,
            alt_email: formData.alt_email,
            home_phone: formData.home_phone,
            cell_phone: formData.cell_phone,
            fax: formData.fax,
            work_phone: formData.work_phone,
            user: {
                user_role: roleEntity.id || roleEntity.documentId
            }
        };

        createTenantUserMutation.mutate(payload);
    };

    return (
        <div className="content">
            <Row className="justify-content-center">
                <Col md="12" className="rounded-0">
                    <Card>
                        <CardHeader>
                            <h5 className="title">{t('userAdd.title') || 'Add New User'}</h5>
                        </CardHeader>
                        <Form onSubmit={handleSubmit}>
                            <CardBody>
                                <Row>
                                    <Col md="6">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.email') || 'Email'} <span style={{ color: 'red' }}>*</span></Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    invalid={!!errors.email}
                                                    placeholder={t('userAdd.placeholders.email') || 'Email'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                                {errors.email && <div className="text-danger small">{errors.email}</div>}
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.password') || 'Password'} <span style={{ color: 'red' }}>*</span></Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="password"
                                                    type="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    invalid={!!errors.password}
                                                    placeholder={t('userAdd.placeholders.password') || 'Password'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                                {errors.password && <div className="text-danger small">{errors.password}</div>}
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.userRole') || 'User Role'} <span style={{ color: 'red' }}>*</span></Label>
                                                <Select
                                                    activeColor={activeColor}
                                                    className="react-select"
                                                    placeholder={t('userAdd.placeholders.selectRole') || 'Select Role'}
                                                    name="user_role"
                                                    isSearchable
                                                    closeMenuOnSelect={true}
                                                    value={rolesData?.results?.find(role => role.documentId === selectedRole || role.id === selectedRole) ?
                                                        { value: selectedRole, label: rolesData.results.find(role => role.documentId === selectedRole || role.id === selectedRole)?.name } : null}
                                                    onChange={(option) => {
                                                        setSelectedRole(option?.value || "");
                                                        if (errors.user_role) {
                                                            setErrors((prev) => ({ ...prev, user_role: undefined }));
                                                        }
                                                    }}
                                                    options={rolesData?.results?.map(role => ({ value: role.documentId || role.id, label: role.name })) || []}
                                                    isDisabled={createTenantUserMutation.isPending || rolesLoading}
                                                    isClearable
                                                />
                                                {errors.user_role && (
                                                    <div className="text-danger small">{errors.user_role}</div>
                                                )}
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.fullName') || 'Full Name'} <span style={{ color: 'red' }}>*</span></Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="full_name"
                                                    value={formData.full_name}
                                                    onChange={handleInputChange}
                                                    invalid={!!errors.full_name}
                                                    placeholder={t('userAdd.placeholders.fullName') || 'Full Name'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                                {errors.full_name && <div className="text-danger small">{errors.full_name}</div>}
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.title') || 'Title'}</Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    placeholder={t('userAdd.placeholders.title') || 'Title'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.address') || 'Address'}</Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleInputChange}
                                                    placeholder={t('userAdd.placeholders.address') || 'Address'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="4">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.salutation') || 'Salutation'}</Label>
                                                <Select
                                                    activeColor={activeColor}
                                                    className="react-select"
                                                    placeholder={t('userAdd.placeholders.select') || 'Select'}
                                                    name="salutation"
                                                    isSearchable={false}
                                                    closeMenuOnSelect={true}
                                                    value={salutationOptions.find(opt => opt === formData.salutation) ?
                                                        { value: formData.salutation, label: formData.salutation } : null}
                                                    onChange={(option) => {
                                                        const value = option?.value || "";
                                                        handleInputChange({ target: { name: "salutation", value } });
                                                    }}
                                                    options={salutationOptions.map(opt => ({ value: opt, label: opt || t('userAdd.placeholders.select') || 'Select' }))}
                                                    isDisabled={createTenantUserMutation.isPending}
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col md="4">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.suffix') || 'Suffix'}</Label>
                                                <Select
                                                    activeColor={activeColor}
                                                    className="react-select"
                                                    placeholder={t('userAdd.placeholders.select') || 'Select'}
                                                    name="suffix"
                                                    isSearchable={false}
                                                    closeMenuOnSelect={true}
                                                    value={suffixOptions.find(opt => opt === formData.suffix) ?
                                                        { value: formData.suffix, label: formData.suffix } : null}
                                                    onChange={(option) => {
                                                        const value = option?.value || "";
                                                        handleInputChange({ target: { name: "suffix", value } });
                                                    }}
                                                    options={suffixOptions.map(opt => ({ value: opt, label: opt || t('userAdd.placeholders.select') || 'Select' }))}
                                                    isDisabled={createTenantUserMutation.isPending}
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col md="4">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.gender') || 'Gender'}</Label>
                                                <Select
                                                    activeColor={activeColor}
                                                    className="react-select"
                                                    placeholder={t('userAdd.placeholders.select') || 'Select'}
                                                    name="gender"
                                                    isSearchable={false}
                                                    closeMenuOnSelect={true}
                                                    value={genderOptions.find(opt => opt === formData.gender) ?
                                                        { value: formData.gender, label: formData.gender } : null}
                                                    onChange={(option) => {
                                                        const value = option?.value || "";
                                                        handleInputChange({ target: { name: "gender", value } });
                                                    }}
                                                    options={genderOptions.map(opt => ({ value: opt, label: opt || t('userAdd.placeholders.select') || 'Select' }))}
                                                    isDisabled={createTenantUserMutation.isPending}
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="4">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.workPhone') || 'Work Phone'} <span style={{ color: 'red' }}>*</span></Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="work_phone"
                                                    value={formData.work_phone}
                                                    onChange={handleInputChange}
                                                    invalid={!!errors.work_phone}
                                                    placeholder={t('userAdd.placeholders.workPhone') || 'Work Phone'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                                {errors.work_phone && <div className="text-danger small">{errors.work_phone}</div>}
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col md="4">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.homePhone') || 'Home Phone'}</Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="home_phone"
                                                    value={formData.home_phone}
                                                    onChange={handleInputChange}
                                                    placeholder={t('userAdd.placeholders.homePhone') || 'Home Phone'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col md="4">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.cellPhone') || 'Cell Phone'}</Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="cell_phone"
                                                    value={formData.cell_phone}
                                                    onChange={handleInputChange}
                                                    placeholder={t('userAdd.placeholders.cellPhone') || 'Cell Phone'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.fax') || 'Fax'}</Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="fax"
                                                    value={formData.fax}
                                                    onChange={handleInputChange}
                                                    placeholder={t('userAdd.placeholders.fax') || 'Fax'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="mb-1">
                                            <FormGroup>
                                                <Label>{t('userAdd.labels.altEmail') || 'Alternative Email'}</Label>
                                                <Input
                                                    style={{ borderRadius: '0px', fontSize: '14px' }}
                                                    name="alt_email"
                                                    value={formData.alt_email}
                                                    onChange={handleInputChange}
                                                    placeholder={t('userAdd.placeholders.altEmail') || 'Alternative Email'}
                                                    disabled={createTenantUserMutation.isPending}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                            <CardFooter className="d-flex justify-content-end gap-2">
                                <Button
                                    outline
                                    onClick={() => navigate(-1)}
                                    color="secondary"
                                    disabled={createTenantUserMutation.isPending}
                                >
                                    {t('userAdd.buttons.cancel') || 'Cancel'}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={createTenantUserMutation.isPending}
                                    style={{
                                        ...activeColorStyle,
                                        border: 'none',
                                        color: 'white',
                                        boxShadow: 'none',
                                        opacity: 1,
                                        fontWeight: '600'
                                    }}
                                >
                                    {createTenantUserMutation.isPending ? t('userAdd.buttons.saving') || 'Saving...' : t('userAdd.buttons.save') || 'Save'}
                                </Button>
                            </CardFooter>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default TenantUserAdd;
